<script setup>
import Vue, {computed, onMounted, reactive, ref, watch, nextTick} from "vue";
import EditEmailTemplate from "@/components/settings/EmailTemplateEdit.vue";
import ImageUpload from "vue-image-crop-upload";
import PrimaryButton from "@/components/ui/PrimaryButton.vue";
import Modal from "@/components/ui/Modal.vue";
import {useStore} from "vue2-helpers/vuex";
import useForm from "@/composables/useForm";
import constants from "@/constants/constants";
import companyService from "@/services/company.service";
import {validEmail} from "@/helpers/string";
import userService from "@/services/user.service";
import teamService from "@/services/team.service";
import useStoreTrigger from "@/composables/useStoreTrigger";
import PasswordChange from "@/components/team/PasswordChange.vue";
import {triggerUpsell} from "@/composables/useStoreActions";

const {profile, groupId} = defineProps({
    profile: {
        type: Boolean,
        default: false,
    },

    groupId: {
        type: Number,
        default: 0,
    }
});

const emit = defineEmits(["saved"]);

const store = useStore();
const storeTrigger = useStoreTrigger();

const user = reactive({
    firstName: '',
    lastName: '',
    email: '',
    jobTitle: '',
    linkedinUrl: '',
    isAdmin: false,
    isExplorer: false,
    external: false,
    newAvatar: null,
});

const {errors, hasErrors, clearErrors, populateForm, resetForm} = useForm(user);

const showModal = ref(false);
const id = ref(0);
const isMainAdmin = ref(false);
const showAvatarUpload = ref(false);
const newGroup = ref(null);
const avatar = ref(null);
const selectedGroups = ref([]);
const loading = ref(false);
const onlyExplorer = ref(false);
const showUpgradeModal = ref(false);
const isCommonUser = ref(false);

const changePassword = ref(null);
const editEmailTemplate = ref(null);

const template = ref({
    subject: '',
    body: ''
});

const title = computed(() => {
    if (profile) {
        return "Edit Profile"
    }

    return (id.value ? 'Edit' : 'Add') + ' ' + (user.isAdmin ? 'Admin' : 'User');
});

const actualGroupsList = computed(() => store.getters.groupsList.filter(group =>
    !selectedGroups.value.some(selectedGroup => selectedGroup.group.id === group.id) &&
    (store.getters.permissions.includes('team-manage') || store.state.user.admin_groups.includes(group.id))
));

const canSave = computed(() => user.firstName?.length && user.email?.length && validEmail(user.email) && (!store.getters.isStartup || user.lastName?.length));

watch(newGroup, (value) => {
    if (value) {
        selectedGroups.value.push({
            group: value,
            role: 'member'
        });

        newGroup.value = null;
    }
})

onMounted(() => {
    if (store.getters.isEnterprise) {
        store.dispatch('fetchGroups');
    }
});

watch(() => user.isExplorer, (value) => {
    if (!value && onlyExplorer.value) {
        showUpgradeModal.value = true;
        showModal.value = false;

        nextTick(() => {
            user.isExplorer = true;
        })
    }
})

watch(showUpgradeModal, (value) => {
    if (!value && !user.isAdmin) {
        showModal.value = true;
    }
})

async function getData() {
    const {data} = await (profile ? userService.getProfileEdit() : teamService.getUserEdit(id.value));

    populateForm(data);
    avatar.value = data.avatar;

    if (!profile) {
        isMainAdmin.value = data.is_main_admin;

        if (user.isExplorer && !data.can_add_user) {
            onlyExplorer.value = true;
        }

        isCommonUser.value = !user.isExplorer;

        data.groups.forEach(selectedGroup => {
            let index = store.getters.groupsList.findIndex(group => group.id === selectedGroup.id);

            if (index >= 0) {
                selectedGroups.value.push({
                    group: store.getters.groupsList[index],
                    role: selectedGroup.role,
                });
            }
        })
    }
}

async function save() {
    loading.value = true;
    clearErrors();

    let form = {...user};

    if (!profile) {
        form.groups = selectedGroups.value.map(group => ({
            id: group.group.id,
            role: group.role,
        }));
    }

    try {
        const {data} = await (profile ? userService.saveProfile(form) : teamService.saveUser(id.value, form, template.value));

        loading.value = false;

        if (!hasErrors(data)) {
            emit("saved");
            showModal.value = false;

            if (!profile) {
                storeTrigger('setNeedUpdateUsers');

                if (data.message) {
                    Vue.notify({
                        group: 'app',
                        type: 'success',
                        duration: 5000,
                        text: data.message
                    })
                }
            }
        }
    } catch (e) {
        loading.value = false;
    }
}

function photoCropped(imageDataUrl) {
    avatar.value = imageDataUrl;
    user.newAvatar = imageDataUrl;
    showAvatarUpload.value = false;
}

function openChangePassword() {
    showModal.value = false;
    changePassword.value.open();
}

async function editTemplate() {
    if (!template.value.subject) {
        const {data} = await companyService.getEmailTemplateEdit('user-invite');
        template.value = data;
    }

    showModal.value = false;
    editEmailTemplate.value.open(template.value);
}

function setTemplate(newTemplate) {
    template.value = newTemplate
    showModal.value = true;
}

async function open(userId = 0, isAdmin = false, isExplorer = false) {
    resetForm();
    selectedGroups.value = [];
    avatar.value = null;
    isMainAdmin.value = false;
    onlyExplorer.value = false;
    isCommonUser.value = false;

    template.value = {
        subject: '',
        body: ''
    };

    id.value = userId;

    if (id.value || profile) {
        showModal.value = true;
        getData();
    } else {
        user.isAdmin = isAdmin;
        user.isExplorer = isExplorer;

        if (store.getters.isEcosystem || (store.getters.isEnterprise && store.state.maxUsers > 0)) {
            const {data} = await teamService.canAddUser();

            if (data) {
                showModal.value = true;
            } else {
                if (store.getters.isEnterprise && store.getters.isPremium) {
                    if (user.isAdmin) {
                        showUpgradeModal.value = true;
                    } else {
                        user.isExplorer = true;
                        user.isAdmin = false;
                        onlyExplorer.value = true;
                        showModal.value = true;
                    }
                } else {
                    triggerUpsell(store);
                }
            }
        } else {
            showModal.value = true;
        }

        if (groupId && !id.value) {
            let index = store.getters.groupsList.findIndex(group => group.id === groupId);

            if (index >= 0) {
                selectedGroups.value.push({
                    group: store.getters.groupsList[index],
                    role: 'member',
                });
            }
        }
    }
}

defineExpose({
    open
});
</script>

<template>
    <div>
        <Modal v-model="showModal" :title="title" modal-class="modal-user" size="md">
            <template #modal-title>
                <div class="d-flex align-items-center">
                    <h4 class="heading4 mb-0 mr-4">{{title}}</h4>

                    <template v-if="!profile && store.getters.isPremium">
                        <div
                            v-if="(store.getters.permissions.includes('team-manage') || !store.getters.isEnterprise) && !isMainAdmin && !store.getters.onlyNeeds && !user.isExplorer"
                            class="input-checkbox"
                        >
                            <input type="checkbox" id="checkbox-admin" v-model="user.isAdmin">
                            <label for="checkbox-admin">Admin</label>
                        </div>

                        <template v-if="!user.isAdmin && store.getters.isEnterprise">
                            <template v-if="!store.getters.onlyNeeds && !user.isExplorer">
                                <div class="input-checkbox ml-3">
                                    <input type="checkbox" id="checkbox-external" v-model="user.external">
                                    <label for="checkbox-external">External</label>
                                </div>

                                <div class="hint-secondary" v-popover.hover.top="{ customClass: 'hint-secondary-popover', content: 'This user will only see content shared explicitly with them or a group they have been added to. By default, they will not see content shared with all users in your company.'}"/>
                            </template>

                            <div v-if="!user.isAdmin & !user.external & !isCommonUser && !store.getters.onlyNeeds" class="input-checkbox ml-3">
                                <input type="checkbox" id="checkbox-explorer" v-model="user.isExplorer">
                                <label for="checkbox-explorer">Explorer</label>
                            </div>
                        </template>
                    </template>
                </div>
            </template>

            <template v-if="!store.getters.isCollinsEnterprise">
                <div class="d-flex align-items-center mb-1">
                    <div class="image-block image-block--rounded--lg mb-4">
                        <img v-if="avatar" :src="avatar" alt="img">
                    </div>
                    <div>
                        <a @click="showAvatarUpload = true" class="btn btn-primary">Upload Photo</a>
                        <p class="modal-text m-0">Image size: 130 X 130 pixels, max file size 5 mb</p>
                    </div>
                </div>

                <ImageUpload
                    v-model="showAvatarUpload"
                    :width="130"
                    :height="130"
                    :noSquare="true"
                    langType="en"
                    @crop-success="photoCropped"
                />
            </template>

            <h5 class="heading5 heading5--secondary">
                First Name*
            </h5>
            <div class="form form-modal mb-4">
                <input type="text" class="form-control form-control--mid" v-model="user.firstName" placeholder="Enter First Name">
            </div>

            <h5 class="heading5 heading5--secondary">
                Last Name<span v-if="store.getters.isStartup">*</span>
            </h5>
            <div class="form form-modal mb-4">
                <input type="text" class="form-control form-control--mid" v-model="user.lastName" placeholder="Enter Last Name">
            </div>

            <h5 class="heading5 heading5--secondary">
                Title
            </h5>
            <div class="form form-modal mb-4">
                <input type="text" class="form-control form-control--mid" v-model="user.jobTitle" placeholder="Enter Title">
            </div>

            <template v-if="!store.getters.isFreeEnterprise">
                <h5 class="heading5 heading5--secondary">
                    Linkedin Profile URL
                </h5>

                <div class="form">
                    <span
                        v-for="(error, index) in errors.linkedinUrl"
                        :key="index"
                        class="form form__error"
                    >
                        {{error}}
                    </span>
                </div>

                <div class="form form-modal mb-4">
                    <input
                        v-model="user.linkedinUrl"
                        type="text"
                        class="form-control form-control--mid"
                        placeholder="Enter Linkedin URL"
                    >
                </div>
            </template>

            <h5 class="heading5 heading5--secondary">
                Email*
            </h5>

            <div class="form">
                <span
                    v-for="(error, index) in errors.email"
                    :key="index"
                    class="form form__error"
                >
                    {{error}}
                </span>
            </div>

            <div class="form form-modal mb-4">
                <input type="text" class="form-control form-control--mid" v-model="user.email" placeholder="Enter Email">
            </div>

            <div class="form form-modal mb-4" v-if="profile && store.state.user.company_id !== constants.companyPfizer && store.state.user.company_id !== constants.companyColgate">
                <a @click="openChangePassword" class="link">Change Password</a>
            </div>

            <template v-if="!profile && !user.isExplorer && store.getters.isEnterprise && !store.getters.isFreeEnterprise">
                <h5 class="heading5 heading5--secondary">
                    Add To Group
                </h5>

                <div class="form form-modal mb-4">
                    <Multiselect
                        v-model="newGroup"
                        :options="actualGroupsList"
                        placeholder="Select Group"
                    />
                </div>

                <div class="dropdown-simple-custom-wrapper">
                    <div v-for="(group, index) in selectedGroups" :key="index">
                        <div class="dropdown-simple-custom-wrapper__group">
                            <div class="dropdown-simple-custom-wrapper__title">
                                {{group.group.name}}<br>
                                {{constants.groupRolesNames[group.role]}}
                            </div>

                            <Dropdown right fill no-caret class="drag-n-drop icons-list dropdown-simple" v-if="store.getters.permissions.includes('team-manage') || (store.state.user.admin_groups.includes(group.group.id) && group.role !== 'admin')">
                                <template v-if="store.getters.permissions.includes('team-manage')">
                                    <DropdownItem v-if="group.role !== 'admin'" class="dropdown-simple-custom-wrapper__admin" @click="selectedGroups[index].role = 'admin'">
                                        Make group admin
                                    </DropdownItem>

                                    <DropdownItem v-else class="dropdown-simple-custom-wrapper__remove-admin" @click="selectedGroups[index].role = 'member'">
                                        Remove As Admin
                                    </DropdownItem>
                                </template>

                                <DropdownItem class="dropdown-simple-custom-wrapper__remove" @click="selectedGroups.splice(index, 1)">
                                    Remove from group
                                </DropdownItem>
                            </Dropdown>
                        </div>
                    </div>
                </div>
            </template>

            <template #modal-footer>
                <div class="buttons-wrapper">
                    <button class="btn btn-bordered btn-lg" v-if="!id && !profile" @click="editTemplate">Customize Invite</button>

                    <PrimaryButton
                        :disabled="!canSave"
                        :loading="loading"
                        @click="save"
                    >
                        Save
                        <template #loading>
                            Saving...
                        </template>
                    </PrimaryButton>
                </div>
            </template>
        </Modal>

        <EditEmailTemplate
            ref="editEmailTemplate"
            @saved="setTemplate"
            @closed="showModal = true"
        />

        <PasswordChange
            ref="changePassword"
            @closed="showModal = true"
        />

        <Modal v-model="showUpgradeModal" no-close>
            <p class="text-center">Your user seat limit has been reached. To add more users, consider upgrading your plan.</p>

            <template #modal-footer>
                <PrimaryButton
                    @click="showUpgradeModal = false"
                >
                    Close
                </PrimaryButton>
            </template>
        </Modal>
    </div>
</template>

<style scoped>

</style>
